import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CompanyDirectorService } from '@app/modules/company-director/services/company-director.service';
import { CompanyDirector } from '@app/modules/company/models/company-director.model';
import { JobService } from '@app/modules/job/services/job.service';
import { OfficerIndividualFormType } from '@app/modules/officer/models/form/officer-individual-form-type';
import { ServiceSupportedCountry } from '@app/modules/service/models/service-supported-country.model';
import {
  CompanyDirectorInput,
  CreateCorporateJobInput,
  DirectorJobInput,
  JobMatterEnum,
  OfficerCompanyInput,
  OfficerDocumentStatusEnum,
  OfficerPersonInput,
  OfficerType,
  OfficerTypeEnum,
  ReplaceDirectorJobInput,
  ServiceEnum,
} from '@generated/graphql';
import { createOfficerForm } from '@shared/classes/officer/officer-form-factory';
import { OfficerInputHelpers } from '@shared/classes/officer/officer-input.helper';
import { startCase } from 'lodash-es';
import { DateTime } from 'luxon';
import { OfficerRequestFormState } from './officer-request-form-state';
import { companyDirectorFactory } from '@app/modules/company/factories/company-director.factory';

@Injectable({
  providedIn: 'any',
})
export class DirectorOfficerRequestForm extends OfficerRequestFormState {
  public nomineeExists = false;

  public editingDirector: CompanyDirector;

  constructor(
    protected directorService: CompanyDirectorService,
    protected officerInputHelpers: OfficerInputHelpers,
    protected jobService: JobService,
  ) {
    super();
  }

  isNominee() {
    return this.selectedOfficerType === OfficerTypeEnum.Nominee;
  }

  public createForm() {
    if (this.selectedOfficerType === OfficerTypeEnum.Nominee) {
      this.formInput = createOfficerForm(OfficerTypeEnum.Individual);
      this.formInput.createOfficerForm();
      this.seedNomineeForm();

      // this.generateRequireDocuments();

      this.scheduleOfficerFormState(false);
    } else {
      super.createForm();

      this.scheduleOfficerFormState(true);
    }
  }

  public seedNomineeForm() {
    this.formInput.officerForm.reset();
    this.formInput.officerForm.patchValue({
      firstName: 'Nominee',
      countryId: this.countryOfIncorporation.country.id,
    });
  }

  public getFormValue(): CompanyDirectorInput {
    const officerFormValue = this.formInput.officerForm.getRawValue();

    if (this.isNominee()) {
      const form = this.formInput
        .officerForm as FormGroup<OfficerIndividualFormType>;

      return this.createDirectorInput(
        OfficerTypeEnum.Individual.toString(),
        'officerPerson',
        {
          firstName: form.controls.firstName.getRawValue(),
          countryId: form.controls.countryId.getRawValue(),
        },
      );
    }

    if ('identification' in officerFormValue) {
      return this.createDirectorInput(
        OfficerTypeEnum.Individual.toString(),
        'officerPerson',
        this.officerInputHelpers.transformOfficerInput(
          officerFormValue,
        ) as OfficerPersonInput,
      );
    }

    return this.createDirectorInput(
      OfficerTypeEnum.Corporate.toString(),
      'officerCompany',
      this.officerInputHelpers.transformOfficerInput(
        officerFormValue,
      ) as OfficerCompanyInput,
    );
  }

  public normalizeExistingOfficerChoices(): this {
    this.existingOfficerState.normalizeDirectorExistingOfficers();

    return this;
  }

  protected setExistingOfficerToForm(officer: OfficerType) {
    if ('identification' in officer.officerProfile) {
      this.patchOfficerPersonFormValue(officer.officerProfile);
    } else if ('representative' in officer.officerProfile) {
      this.patchOfficerCompanyFormValue(officer.officerProfile);
    }

    if (
      this.mode === 'Replace' &&
      this.existingOfficerState.selectedOfficerId
    ) {
      this.checkNeedDocument(officer);
    }

    this.formInput.officerForm.updateValueAndValidity();
    this.scheduleOfficerFormState(false);
  }

  public setEditData(officer: CompanyDirector): this {
    this.editingDirector = officer;

    const officerProfile = officer.officer.officerProfile;

    if ('identification' in officerProfile) {
      // INFO: officer person
      this.setOfficerPersonOfficerData(officerProfile);
    } else if ('representative' in officerProfile) {
      // INFO: officer company
      this.setOfficerCompanyOfficerData(officerProfile);
    }

    return this;
  }

  private checkNeedDocument(officer: OfficerType) {
    this.needDocument = !officer.officerDocuments?.every(
      (document) => document.status === OfficerDocumentStatusEnum.Verified,
    );
  }

  private createDirectorInput(
    officerType: string,
    inputKey: 'officerPerson' | 'officerCompany',
    officerInput: OfficerPersonInput | OfficerCompanyInput | {
      firstName: string;
      countryId: string;
    },
  ): CompanyDirectorInput {
    return {
      dateFrom: this.effectiveDate.value || DateTime.now().toJSDate(),
      isNominee: this.isNominee(),
      officerType,
      [inputKey]: officerInput,
    };
  }

  private resolveEffectiveDate(): string {
    return DateTime.fromJSDate(this.effectiveDate.value).toISODate();
  }

  private createNewDirector() {
    const directorInput = this.createDirectorJobInput();
    const serviceName = this.isNominee()
      ? ServiceEnum.NomineeDirector
      : ServiceEnum.AddDirector;

    if (!this.activeUser.isCustomer()) {
      this.submitExpertCreateDirector(directorInput);
      return;
    }

    this.submitCustomerDirectorJob(
      serviceName,
      JobMatterEnum.AddDirector,
      directorInput,
    );
  }

  private createDirectorJobInput(): DirectorJobInput {
    const directorInput: DirectorJobInput = {
      companyId: this.company.id,
      director: {
        id: this.existingOfficerState.selectedOfficerId ?? null,
        ...this.getFormValue(),
        dateFrom: this.resolveEffectiveDate(),
      },
    };

    if (!this.isNominee()) {
      directorInput.documents = this.uploadedDocuments;
    }

    if (this.existingOfficerState.selectedOfficerId) {
      directorInput.director.id = this.existingOfficerState.selectedOfficerId;
      directorInput.director.refId =
        this.existingOfficerState.selectedOfficerId;
    }

    return directorInput;
  }

  private updateDirector() {
    const directorInput = this.createUpdateDirectorInput();

    if (!this.activeUser.isCustomer()) {
      this.submitExpertUpdateDirector(directorInput);
      return;
    }

    this.submitCustomerDirectorJob(
      ServiceEnum.EditDirector,
      JobMatterEnum.EditDirector,
      directorInput,
      {
        oldDirector: this.editingDirector,
      },
    );
  }

  private createUpdateDirectorInput(): DirectorJobInput {
    const directorInput: DirectorJobInput = {
      director: {
        id: this.editingDirector.id,
        ...this.getFormValue(),
        dateFrom: this.resolveEffectiveDate(),
      },
    };

    directorInput.documents = this.uploadedDocuments;

    return directorInput;
  }

  protected getReplaceDirectorInput() {
    const directorInput: ReplaceDirectorJobInput = {
      companyId: this.company.id,
      director: {
        id: this.existingOfficerState.selectedOfficerId ?? null,
        ...this.getFormValue(),
        dateFrom: this.resolveEffectiveDate(),
      },
    };

    directorInput.documents = this.uploadedDocuments;

    return directorInput;
  }

  private replaceDirector() {
    const directorInput = this.getReplaceDirectorInput();

    if (!this.activeUser.isCustomer()) {
      directorInput.effectiveDate = this.resolveEffectiveDate();
      directorInput.replacedId = this.editingDirector.id;
      this.submitExpertReplaceDirector(directorInput);
      return;
    }

    this.submitCustomerDirectorJob(
      ServiceEnum.ReplaceDirector,
      JobMatterEnum.ReplaceDirector,
      directorInput,
      {
        oldDirector: this.editingDirector,
      },
    );
  }

  private submitExpertCreateDirector(directorInput: DirectorJobInput): void {
    this.postStatus$.next('SUBMITTING');

    this.directorService.expertCreateDirector(directorInput).subscribe({
      next: (result) => {
        this.result$.next(companyDirectorFactory.tryCreating(result));
        this.postStatus$.next('SUCCESS');
      },
      error: (error) => {
        this.postStatus$.next('ERROR');
        return error;
      },
      complete: () => this.postStatus$.next('FINISHED'),
    });
  }

  private submitExpertUpdateDirector(directorInput: DirectorJobInput): void {
    this.postStatus$.next('SUBMITTING');

    this.directorService.expertUpdateDirector(directorInput).subscribe({
      next: (result) => {
        this.result$.next(companyDirectorFactory.tryCreating(result));
        this.postStatus$.next('SUCCESS');
      },
      error: (error) => {
        this.postStatus$.next('ERROR');
        return error;
      },
      complete: () => this.postStatus$.next('FINISHED'),
    });
  }

  private submitExpertReplaceDirector(
    directorInput: ReplaceDirectorJobInput,
  ): void {
    this.postStatus$.next('SUBMITTING');

    this.directorService.expertReplaceDirector(directorInput).subscribe({
      next: () => this.postStatus$.next('SUCCESS'),
      error: (error) => {
        this.postStatus$.next('ERROR');
        return error;
      },
      complete: () => this.postStatus$.next('FINISHED'),
    });
  }

  private submitCustomerDirectorJob(
    serviceName: ServiceEnum,
    jobMatter: JobMatterEnum,
    directorInput: DirectorJobInput | ReplaceDirectorJobInput,
    extraDetail: Record<string, unknown> = {},
  ): void {
    const jobInput = this.createCustomerDirectorJobInput(
      serviceName,
      jobMatter,
      directorInput,
      extraDetail,
    );

    this.postStatus$.next('SUBMITTING');

    this.jobService.createCorporateJob(jobInput).subscribe({
      next: (job) => {
        this.createdJob = job;
        this.postStatus$.next('SUCCESS');
      },
      error: (error) => {
        this.postStatus$.next('ERROR');
        return error;
      },
      complete: () => {
        this.postStatus$.next('FINISHED');
      },
    });
  }

  private createCustomerDirectorJobInput(
    serviceName: ServiceEnum,
    jobMatter: JobMatterEnum,
    directorInput: DirectorJobInput | ReplaceDirectorJobInput,
    extraDetail: Record<string, unknown> = {},
  ): CreateCorporateJobInput {
    this.jobOrderState.addOrUpdateService(serviceName, 'mainServices');

    const jobInput: CreateCorporateJobInput = {
      serviceName,
      jobInput: {
        matter: startCase(jobMatter),
        companyId: this.company.id,
        serviceSupportedCountryId: this.serviceSupportedCountry.id,
      },
      directorInput,
    };

    jobInput.jobInput.detail = JSON.stringify({
      ...jobInput,
      jobServiceOrder: this.jobOrderState.jobServicesOrder,
      ...extraDetail,
    });

    return jobInput;
  }

  public submitForm() {
    if (this.mode === 'New') {
      this.createNewDirector();
    } else if (this.mode === 'Edit') {
      this.updateDirector();
    } else if (this.mode === 'Replace') {
      this.replaceDirector();
    }
  }

  public setServiceSupportedCountry(services: ServiceSupportedCountry[]): this {
    let serviceName = ServiceEnum.AddDirector;

    if (this.mode === 'Edit') {
      serviceName = ServiceEnum.EditDirector;
    } else if (this.mode === 'Replace') {
      serviceName = ServiceEnum.ReplaceDirector;
    }

    this.serviceSupportedCountry = services.find(
      (s) =>
        s.supportedCountry.id === this.countryOfIncorporation.id &&
        s.service.name === serviceName,
    );

    return this;
  }
}
